
import EventEmitter from 'events';
import Intersection from './intersection';

const clipperScale = 1e7;

export default class Polygon extends EventEmitter {
  constructor (points, innerPoints, innerOffsetPoints, options = {}) {
    super();

    this.points = points;
    this.innerPoints = Array.isArray(innerPoints) ? innerPoints : [];
    this.innerOffsetPoints = Array.isArray(innerOffsetPoints) ? innerOffsetPoints : [];
    this.options = options;

    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.position = new THREE.Vector2();
    this.rotation = 0;

    this.element = this.createGroupSVG('polygon');

    this.polyline = this.createPolylineSVG(this.options);
    this.updatePolyline(this.polyline, this.points);
    this.element.appendChild(this.polyline);

    // Create inner polylines (holes)
    // this.innerPolylines = this.innerOffsetPoints.map(innerPath => {
    //   let polyline = this.createInnerPolylineSVG({
    //     strokeColor: '#999',
    //     strokeWidth: 5
    //   });
    //   this.updatePolyline(polyline, innerPath); // Update each inner polyline with the respective points
    //   this.element.appendChild(polyline);
    //   return polyline;
    // });


  }

  toClipperCoordinates (points) {
    let result = points.map(p => {
      return {
        X: p.x * clipperScale,
        Y: p.y * clipperScale
      };
    });
    return result;
  }

  toNestCoordinates (path) {
    return path.map(p => {
      return new THREE.Vector2(p.X / clipperScale, p.Y / clipperScale);
    });
  }

  // http://www.angusj.com/delphi/clipper/documentation/Docs/Units/ClipperLib/Classes/ClipperOffset/_Body.htm
  offset (points, offset = 1, miterLimit = 2.5, curveTolerance = 1.0) {
    let clipper = new ClipperLib.ClipperOffset(miterLimit, curveTolerance * clipperScale);

    let path = this.toClipperCoordinates(points);
    clipper.AddPath(path, ClipperLib.JoinType.jtSquare, ClipperLib.EndType.etClosedPolygon);
    let paths = new ClipperLib.Paths();
    
    // suppressing window alert function temporarily during clipper Execute
    const originalAlert = window.alert;
    window.alert = function() {};

    try {
      clipper.Execute(paths, offset * clipperScale);

    } finally {
      // reuse window alert function
      window.alert = originalAlert;
    }

    if(!paths[0])
    {
      return null;
    }

    return this.toNestCoordinates(paths[0]);
    
  }

  moveTo (x, y) {
    this.position.x = x;
    this.position.y = y;
    this.update();
  }

  move (dx, dy) {
    this.moveTo(this.position.x + dx, this.position.y + dy);
  }

  rotateTo (r) {
    this.rotation = r;
    this.update();
  }

  rotate (dr) {
    this.rotation += dr;
    this.update();
  }

  update () {
    this.updatePolyline(this.polyline, this.points);
  }

  updatePolyline (polyline, points, center) {
    polyline.points.clear();

    let transformed = this.transform(points, center);
    transformed.forEach(p => {
      let point = this.svg.createSVGPoint();
      point.x = p.x;
      point.y = p.y;
      polyline.points.appendItem(point);
    });

    if ((this.options.closed === undefined || this.options.closed) && transformed.length > 0) {
      let p = transformed[0];
      let point = this.svg.createSVGPoint();
      point.x = p.x;
      point.y = p.y;
      polyline.points.appendItem(point);
    }
  }

  updateText (text, point, center) {
    let transformed = this.transform([point], center);
    transformed.forEach(p => {
      text.setAttributeNS(null, 'x', p.x);
      text.setAttributeNS(null, 'y', p.y);
    });
  }

  createGroupSVG (className = '') {
    let g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    if (className.length > 0) { g.classList.add(className); }
    return g;
  }

  createPolylineSVG (options = {}) {
    let polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('stroke', options.strokeColor || '#000000');
    polyline.setAttribute('stroke-width', options.strokeWidth || 2);

    return polyline;
  }

  createInnerPolylineSVG (options = {}) {
    let polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('stroke', options.strokeColor || '#000000');
    polyline.setAttribute('stroke-width', options.strokeWidth || 5);
    polyline.setAttribute('fill', 'black')

    return polyline;
  }

  center (points) {
    let c = new THREE.Vector2();
    points.forEach(p => {
      c.add(p);
    });
    return c.divideScalar(points.length);
  }

  transform (points, center) {
    if (center === undefined) {
      center = this.center(points);
    }

    return points.map(p => {
      p = p.clone().rotateAround(center, this.rotation);
      return new THREE.Vector2(p.x + this.position.x, p.y + this.position.y);
    });
  }

  intersects (polygon) {
    let points0 = this.transform(this.points);
    let points0Inner = this.innerOffsetPoints.map(innerPath => this.transform(innerPath));
    let points1 = polygon.transform(polygon.points);
    let points1Inner = polygon.innerOffsetPoints.map(innerPath => polygon.transform(innerPath));

    // Check if any of the points of points1 are inside the inner polygons of points0
    let allPoints1InsidePolygon0InnerHoles = true;
    for (let i = 0; i < points0Inner.length; i++) {
      const innerPath = points0Inner[i];
      for (let j = 0; j < points1.length; j++) {
          const point = points1[j];
          if (innerPath && !this.contains(innerPath, point)) {
              // If the point is not inside the inner hole, the polygon is not inside the inner hole, this check is over,
              allPoints1InsidePolygon0InnerHoles = false;
              break;              
          }
      }
      if (!allPoints1InsidePolygon0InnerHoles) break;
    }
    if (allPoints1InsidePolygon0InnerHoles && points0Inner.length > 0)
    {
      return false;
    }

    let allPoints0InsidePolygon1InnerHoles = true;
    for (let i = 0; i < points1Inner.length; i++) {
      const innerPath = points1Inner[i];
      for (let j = 0; j < points0.length; j++) {
          const point = points0[j];
          if (innerPath && !this.contains(innerPath, point)) {
              // If the point is not inside the inner hole, the polygon is not inside the inner hole, this check is over,
              allPoints0InsidePolygon1InnerHoles = false;
              break;  
          }
      }
      if (!allPoints0InsidePolygon1InnerHoles) break;
    }

    if (!allPoints0InsidePolygon1InnerHoles && points1Inner.length > 0)
    {
      return false;
    }

    const found = points0.find(p => Intersection.contains(points1, p));
    if (found !== undefined) {
      return true;
    }
    return Intersection.intersects(points0, points1);
  }

  bounds (transform = false) {
    const points = transform ? this.transform(this.points) : this.points;

    let minX = 0;
    let minY = 0;
    let maxX = 0;
    let maxY = 0;

    // set head point as start
    if (points.length > 0) {
      let head = points[0];
      minX = head.x;
      maxX = head.x;
      minY = head.y;
      maxY = head.y;
    }

    points.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  area () {
    if (this._area > 0) return this._area;

    let area = 0;
    let n = this.points.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      area += (this.points[j].x + this.points[i].x) * (this.points[j].y - this.points[i].y);
    }
    this._area = 0.5 * area;
    return this._area;
  }

  dispose () {
    this.element.remove();
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/nesting/polygon.js